import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DetailsConfig, SharedUiDetailsModule } from '@snelstart/ui-details';
import {
  BokkzAlgDataService,
  BokkzMumaClient,
  BokkzMumaDataService,
  ClientEntity,
  MumaEntity,
  MumaEntityMachtigingStatus,
  MumaRequest,
  MumaRequestDienst,
  MumaRequestMutatieType,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {ToastService} from "@snelstart/ui-toast";
import {FormBuilder} from "@angular/forms";
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import {Observable} from "rxjs";
import {ActivatedRoute, Params} from "@angular/router";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { MumaComponent } from '../muma/muma.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-muma-details',
  templateUrl: './feature-tax-muma-details.component.html',
  styleUrl: './feature-tax-muma-details.component.scss',
  imports: [
    SharedUiDialogModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    MumaComponent,
    CommonModule
  ]
})
export class FeatureTaxMumaDetailsComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  protected clientId: any;
  private destroy$ = new Subject<void>();
  public mumaEntity = {} as MumaEntity;
  public clientEntity = {} as ClientEntity;
  public isOpvoeren = false;
  public isActiveren = false;
  public isIntrekken = false;
  public activatiecode: any;
  public titelMachtiging = '';
  public dialogDelete = false;

  constructor(
    public algDataService: BokkzAlgDataService,
    public dataService: BokkzMumaDataService,
    public bokkzMumaClient: BokkzMumaClient,
    public toastService: ToastService,
    public activatedRoute: ActivatedRoute,
    public fb: FormBuilder,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit(): void {
    this.algDataService.client.pipe(takeUntil(this.destroy$)).subscribe((client) => {
      this.clientId = client.sstRelationId
    })
    this.dataService.mumaEntity.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        if (value) {
          this.mumaEntity = value;
        }
      }
    );
    this.dataService.isOpvoeren.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isOpvoeren = value;
    })
    this.dataService.isActiveren.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isActiveren = value;
    })
    this.dataService.isIntrekken.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isIntrekken = value;
    })
    this.dataService.activatieCode.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        if (value) {
          this.activatiecode = value;
        }
      }
    );
    this.route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe((params: Params) => {  // Voeg `: Params` toe
      this.setTitelMachtiging();
    });
  }

  ngOnDestroy(): void {  // Voeg dit toe
    this.destroy$.next();
    this.destroy$.complete();
  }

  public configOpvoeren() {
    return ButtonConfiguration.create({
      onClick: () =>
        new Observable((subscription) => {
          setTimeout(() => {
            subscription.next(true);
            subscription.complete();
            this.postEntity(MumaRequestMutatieType.Opvoeren)
          }, 200);
        }),
    })
  }

  public configActiveren() {
    return ButtonConfiguration.create({
      onClick: () =>
        new Observable((subscription) => {
          setTimeout(() => {
            subscription.next(true);
            subscription.complete();
            this.postEntity(MumaRequestMutatieType.Activeren)
          }, 200);
        }),
    })
  }

  public configIntrekken() {
    return ButtonConfiguration.create({
      onClick: () =>
        new Observable((subscription) => {
          setTimeout(() => {
            subscription.next(true);
            subscription.complete();
            this.postEntity(MumaRequestMutatieType.Intrekken)
            this.dialogDelete = false
          }, 200);
        }),
    })
  }

  public configViaOphalen() {
    return ButtonConfiguration.create({
      onClick: () =>
        new Observable((subscription) => {
          setTimeout(() => {
            subscription.next(true);
            subscription.complete();
            this.postEntity(MumaRequestMutatieType.Via_verzoek)
            this.dialogDelete = false
          }, 200);
        }),
    })
  }

  public machtigingBevatViaMachtiging() {
    return this.mumaEntity.mumaRequest?.dienst === MumaRequestDienst.DoMa_IH ||
        this.mumaEntity.mumaRequest?.dienst === MumaRequestDienst.VIA_2024 ||
        this.mumaEntity.mumaRequest?.dienst === MumaRequestDienst.VIA_2025
  }

  public viaOphalenIsEnabled(): boolean {
    if (this.mumaEntity.machtigingStatus !== MumaEntityMachtigingStatus.ACTIEF) {
      return false;
    }

    const dienst = this.mumaEntity.mumaRequest?.dienst;
    const today = new Date();

    switch (dienst) {
      case MumaRequestDienst.VIA_2024:
        return today >= new Date("2025-03-01");  // true als vandaag na 1 maart 2025
      case MumaRequestDienst.VIA_2025:
        return today >= new Date("2026-03-01");  // true als vandaag na 1 maart 2026
      case MumaRequestDienst.VIA_2026:
        return today >= new Date("2027-03-01");  // true als vandaag na 1 maart 2027
      default:
        return false;
    }
  }

  protected readonly detailsConfig = DetailsConfig.create({
    hideArrowButtons: true,
  });

  public postEntity(mutatieType: MumaRequestMutatieType) {
    let bsn = this.mumaEntity.taxPayerFiscalNumber;
    if (bsn.includes("B")) {
      bsn = bsn.split("B")[0];
    }
    this.mumaEntity.mumaRequest = {
      bsn: bsn,
      dienst: this.mumaEntity.mumaRequest?.dienst,
      activatieToken: this.isActiveren ? this.activatiecode : null as unknown as string,
      mutatieType: mutatieType,
      gemachtigdeIdentiteitsNummer: this.mumaEntity.mumaRequest?.gemachtigdeIdentiteitsNummer,
    } as MumaRequest;
    this.mumaEntity.machtigingStatus = MumaEntityMachtigingStatus.CONTROLEREN
    this.bokkzMumaClient.postOneMuma(this.clientId, true, this.mumaEntity).subscribe({
      next: () => {
        this.dataService.updateMumaEntity(this.mumaEntity);
      },
      error: () => {
        console.log('error');
        this.toastService.showErrorMessage("Het verzoek is niet succesvol aangemaakt.")
      },
      complete: () => {
        this.toastService.showSuccessMessage("Het verzoek is succesvol aangemaakt en wordt binnen 24 uur behandeld.")
      }
    })
  }

  showDeleteInfo() {
    this.dialogDelete = true
  }

  private setTitelMachtiging(): void {
    const type = this.mumaEntity.type;
    if (!type) return;

    const parts = type.split('_');
    const prefix = parts[0];
    const subtype = parts[1];
    const year = parts[2];

    switch (prefix) {
      case 'SBA': {
        const belastingType =    subtype === 'VpB' ? 'vennootschapsbelasting' :
                                                                  subtype === 'IH' ? 'inkomstenbelasting' :
                                                                  subtype === 'OB' ? 'omzetbelasting' :
                                                                  subtype === 'TSG' ? 'toeslagen' : '';

        if (subtype === 'TSG') {
          this.titelMachtiging = `Machtiging servicebericht toeslagen\n${year}`;
        } else {
          this.titelMachtiging = `Machtiging servicebericht aanslag\n${belastingType} ${year}`;
        }
        break;
      }

      case 'VIA': {
        this.titelMachtiging = `Machtiging gegevens vooraf ingevulde aangifte\n${subtype}`;
        break;
      }

      case 'DoMa': {
        const domaType = subtype === 'IH' ? 'inkomstenbelasting' :
          subtype === 'OB' ? 'omzetbelasting' :
            subtype === 'VpB' ? 'vennootschapsbelasting' :
              subtype === 'TSG' ? 'toeslagen' : '';

        this.titelMachtiging = subtype === 'VpB' ?
          `Doorlopende machtiging\n${domaType}` :
          `Doorlopende machtiging ${domaType}`;
        break;
      }

      default:
        this.titelMachtiging = '';
    }
  }

}
